import React, { useState, useEffect, useCallback } from 'react';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog, PageLoader, EmptyState, Badge, ActionBtn, SectionHeader, FormField, Pagination } from '../components/common/UI';
import { TASK_STATUS, TASK_TYPES, EQUIPMENT_STATUS, EQUIPMENT_TYPES, getStatusConfig, getStatusLabel } from '../utils/statusHelpers';
import { toShamsi, daysUntil, formatCurrency } from '../utils/dateHelpers';
import { useAuth } from '../context/AuthContext';
import ShamsiDatePicker from '../components/common/ShamsiDatePicker';
import { MdAdd, MdEdit, MdDelete, MdCheckCircle, MdVisibility } from '../utils/icons';
import { FaCheckCircle, FaVideo, FaTools } from 'react-icons/fa';

// ======================== TASKS ========================
const taskInitial = { project: '', title: '', type: 'Editing', assignedTo: '', deadline: '', status: 'Pending', priority: 'Medium', notes: '' };

export function Tasks() {
  const { isManager } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(taskInitial);
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1); // پیج‌بندی (صفحه‌بندی سمت کلاینت) - تسک‌ها

  const load = useCallback(async () => {
    try {
      const [taskRes, projRes, empRes] = await Promise.all([
        API.get('/tasks' + (filterStatus ? `?status=${filterStatus}` : '')),
        API.get('/projects'),
        API.get('/employees'),
      ]);
      setTasks(taskRes.data);
      setProjects(projRes.data);
      setEmployees(empRes.data);
    } finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [filterStatus]);

  const openAdd = () => { setForm(taskInitial); setEditItem(null); setShowModal(true); };
  const openEdit = (t) => {
    setForm({ project: t.project?._id || '', title: t.title, type: t.type, assignedTo: t.assignedTo?._id || '', deadline: t.deadline?.split('T')[0] || '', status: t.status, priority: t.priority, notes: t.notes || '' });
    setEditItem(t); setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.project || !form.title) return toast.error('پروژه و عنوان ضروری است');
    setSaving(true);
    try {
      if (editItem) { await API.put(`/tasks/${editItem._id}`, form); toast.success('تسک بروزرسانی شد'); }
      else { await API.post('/tasks', form); toast.success('تسک اضافه شد'); }
      setShowModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'خطا'); } finally { setSaving(false); }
  };

  const quickDone = async (id) => {
    try { await API.put(`/tasks/${id}`, { status: 'Done' }); toast.success('تسک انجام شد'); load(); }
    catch { toast.error('خطا'); }
  };

  const handleDelete = async () => {
    try { await API.delete(`/tasks/${deleteId}`); toast.success('تسک حذف شد'); setDeleteId(null); load(); }
    catch { toast.error('خطا'); }
  };

  const priorityConfig = { Low: 'bg-slate-100 text-slate-700', Medium: 'bg-yellow-100 text-yellow-700', High: 'bg-red-100 text-red-700' };
  const priorityLabel = { Low: 'کم', Medium: 'متوسط', High: 'زیاد' };

  if (loading) return <PageLoader />;

  const TASK_PAGE_SIZE = 10;
  const taskTotalPages = Math.max(1, Math.ceil(tasks.length / TASK_PAGE_SIZE));
  const pagedTasks = tasks.slice((page - 1) * TASK_PAGE_SIZE, page * TASK_PAGE_SIZE);

  return (
    <div className="space-y-5 fade-in">
      <SectionHeader title="تسک‌ها" subtitle={`${tasks.length} تسک`}
        actions={isManager && <ActionBtn onClick={openAdd} icon={<MdAdd size={16} />} label="تسک جدید" />} />

      <div className="glass-card p-4">
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="form-input w-full sm:w-48">
          <option value="">همه وضعیت‌ ها</option>
          {Object.entries(TASK_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {tasks.length === 0 ? <EmptyState icon="📋" title="تسکی یافت نشد" action={isManager && <ActionBtn onClick={openAdd} icon={<MdAdd size={16} />} label="تسک جدید" />} /> : (
        <>
          <div className="glass-card overflow-hidden hidden md:block">
            <div className="overflow-x-auto">
              <table className="premium-table">
                <thead><tr><th>عنوان</th><th>پروژه</th><th>نوع</th><th>مسئول</th><th>دیدلاین</th><th>اولویت</th><th>وضعیت</th><th>عملیات</th></tr></thead>
                <tbody>
                  {pagedTasks.map(t => (
                    <tr key={t._id}>
                      <td className="font-medium text-gray-800">{t.title}</td>
                      <td className="text-gray-500 text-sm">{t.project?.name || '---'}</td>
                      <td className="text-gray-600 text-sm">{TASK_TYPES[t.type] || t.type}</td>
                      <td className="text-gray-600">{t.assignedTo?.name || '---'}</td>
                      <td>
                        {t.deadline ? <div>
                          <div className="text-gray-600 text-sm">{toShamsi(t.deadline)}</div>
                          {t.status !== 'Done' && <div className={`text-xs ${daysUntil(t.deadline) < 0 ? 'text-red-600' : daysUntil(t.deadline) <= 1 ? 'text-yellow-600' : 'text-gray-600'}`}>
                            {daysUntil(t.deadline) < 0 ? `${Math.abs(daysUntil(t.deadline))} روز تأخیر` : `${daysUntil(t.deadline)} روز`}
                          </div>}
                        </div> : '---'}
                      </td>
                      <td><span className={`badge ${priorityConfig[t.priority]}`}>{priorityLabel[t.priority]}</span></td>
                      <td><Badge statusConfig={TASK_STATUS[t.status]} /></td>
                      <td>
                        <div className="flex items-center gap-1">
                          {t.status !== 'Done' && <button onClick={() => quickDone(t._id)} className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-100 rounded-lg transition-all" title="انجام شد"><MdCheckCircle size={15} /></button>}
                          {isManager && <button onClick={() => openEdit(t)} className="p-1.5 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-100 rounded-lg transition-all"><MdEdit size={15} /></button>}
                          {isManager && <button onClick={() => setDeleteId(t._id)} className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-100 rounded-lg transition-all"><MdDelete size={15} /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="md:hidden space-y-3">
            {pagedTasks.map(t => (
              <div key={t._id} className="mobile-card">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-semibold text-gray-800">{t.title}</div>
                    <div className="text-xs text-gray-500">{t.project?.name} • {t.assignedTo?.name || 'بدون مسئول'}</div>
                  </div>
                  <Badge statusConfig={TASK_STATUS[t.status]} />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className={`badge ${priorityConfig[t.priority]}`}>{priorityLabel[t.priority]}</span>
                  {t.deadline && <span className="text-gray-500 text-xs">{toShamsi(t.deadline)}</span>}
                </div>
                {t.status !== 'Done' && (
                  <button onClick={() => quickDone(t._id)} className="w-full mt-2 py-1.5 text-xs bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors">✅ انجام شد</button>
                )}
              </div>
            ))}
          </div>
          <Pagination page={page} totalPages={taskTotalPages} onChange={setPage} />
        </>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editItem ? 'ویرایش تسک' : 'تسک جدید'} size="md">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label="پروژه" required>
            <select className="form-input" value={form.project} onChange={e => setForm({ ...form, project: e.target.value })}>
              <option value="">انتخاب پروژه</option>
              {projects.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
          </FormField>
          <FormField label="نوع تسک">
            <select className="form-input" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
              {Object.entries(TASK_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </FormField>
          <div className="sm:col-span-2">
            <FormField label="عنوان" required><input className="form-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="عنوان تسک..." /></FormField>
          </div>
          <FormField label="مسئول">
            <select className="form-input" value={form.assignedTo} onChange={e => setForm({ ...form, assignedTo: e.target.value })}>
              <option value="">انتخاب کارمند</option>
              {employees.map(e => <option key={e._id} value={e._id}>{e.name} ({e.role})</option>)}
            </select>
          </FormField>
          <FormField label="دیدلاین">
            <ShamsiDatePicker value={form.deadline} onChange={date => setForm({ ...form, deadline: date })} className="form-input" />
          </FormField>
          <FormField label="اولویت">
            <select className="form-input" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
              <option value="Low">کم</option>
              <option value="Medium">متوسط</option>
              <option value="High">زیاد</option>
            </select>
          </FormField>
          <FormField label="وضعیت">
            <select className="form-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              {Object.entries(TASK_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </FormField>
        </div>
        <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-200">
          <button onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors text-sm">انصراف</button>
          <ActionBtn onClick={handleSave} label={saving ? 'ذخیره...' : editItem ? 'بروزرسانی' : 'ذخیره'} disabled={saving} />
        </div>
      </Modal>
      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="حذف تسک" message="آیا مطمئن هستید؟" />
    </div>
  );
}

// ======================== EQUIPMENT ========================
const eqInitial = { name: '', type: 'Camera', brand: '', model: '', status: 'Available', purchaseDate: '', purchasePrice: '', remainingPrice: '', purchaseFrom: 'دفتر', notes: '' };

export function Equipment() {
  const { isManager } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [form, setForm] = useState(eqInitial);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1); // پیج‌بندی (صفحه‌بندی سمت کلاینت) - تجهیزات

  const load = useCallback(async () => {
    try { const { data } = await API.get('/equipment'); setItems(data); } 
    catch (err) { toast.error('خطا در بارگذاری'); } 
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm(eqInitial); setEditItem(null); setShowModal(true); };
  const openEdit = (e) => {
    setForm({
      name: e.name, type: e.type, brand: e.brand || '', model: e.model || '',
      status: e.status, purchaseDate: e.purchaseDate?.split('T')[0] || '',
      purchasePrice: e.purchasePrice || 0, purchaseFrom: e.purchaseFrom || 'دفتر',
      notes: e.notes || ''
    });
    setEditItem(e); setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name) return toast.error('نام تجهیز ضروری است');
    setSaving(true);
    try {
      if (editItem) { 
        await API.put(`/equipment/${editItem._id}`, form); 
        toast.success('تجهیز بروزرسانی شد'); 
      } else { 
        await API.post('/equipment', form); 
        toast.success('تجهیز اضافه شد'); 
      }
      setShowModal(false); 
      load();
    } catch (err) { 
      toast.error(err.response?.data?.message || 'خطا'); 
    } finally { 
      setSaving(false); 
    }
  };

  const handleDelete = async () => {
    try { 
      await API.delete(`/equipment/${deleteId}`); 
      toast.success('تجهیز حذف شد'); 
      setDeleteId(null); 
      load(); 
    } catch { 
      toast.error('خطا'); 
    }
  };

  const formatPrice = (price) => {
    if (!price) return '---';
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' افغانی';
  };

  const counts = { 
    Available: items.filter(i => i.status === 'Available').length, 
    InUse: items.filter(i => i.status === 'InUse').length, 
    Maintenance: items.filter(i => i.status === 'Maintenance').length 
  };

  const totalValue = items.reduce((sum, i) => sum + (i.remainingPrice || i.purchasePrice || 0), 0);

  if (loading) return <PageLoader />;

  const EQ_PAGE_SIZE = 10;
  const eqTotalPages = Math.max(1, Math.ceil(items.length / EQ_PAGE_SIZE));
  const pagedItems = items.slice((page - 1) * EQ_PAGE_SIZE, page * EQ_PAGE_SIZE);

  return (
    <div className="space-y-5 fade-in">
      <SectionHeader 
        title="تجهیزات" 
        subtitle={`${items.length} تجهیز | ارزش کل: ${formatPrice(totalValue)}`}
        actions={isManager && <ActionBtn onClick={openAdd} icon={<MdAdd size={16} />} label="تجهیز جدید" />} 
      />

      <div className="grid grid-cols-4 gap-3">
        {[
          { icon: FaCheckCircle, label: 'موجود', value: counts.Available, color: 'green' },
          { icon: FaVideo, label: 'در استفاده', value: counts.InUse, color: 'blue' },
          { icon: FaTools, label: 'تعمیر', value: counts.Maintenance, color: 'red' },
          { icon: FaCheckCircle, label: 'ارزش کل', value: formatPrice(totalValue), color: 'purple' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className={`glass-card p-4 text-center border ${
            color === 'green' ? 'border-green-200' : 
            color === 'blue' ? 'border-blue-200' : 
            color === 'red' ? 'border-red-200' : 'border-purple-200'
          }`}>
            <div className="flex justify-center mb-2">
              <Icon className={`text-2xl ${
                color === 'green' ? 'text-green-600' : 
                color === 'blue' ? 'text-blue-600' : 
                color === 'red' ? 'text-red-600' : 'text-purple-600'
              }`} />
            </div>
            <div className="text-xl font-bold text-gray-800">{value}</div>
            <div className="text-xs text-gray-500">{label}</div>
          </div>
        ))}
      </div>

      {items.length === 0 ? (
        <EmptyState 
          icon="🎥" 
          title="تجهیزی یافت نشد" 
          subtitle="اولین تجهیز خود را اضافه کنید"
          action={isManager && <ActionBtn onClick={openAdd} icon={<MdAdd size={16} />} label="تجهیز جدید" />} 
        />
      ) : (
        <>
          {/* Desktop Table */}
          <div className="glass-card overflow-hidden hidden md:block">
            <div className="overflow-x-auto">
              <table className="premium-table">
                <thead>
                  <tr>
                    <th>نام</th><th>نوع</th><th>برند/مدل</th><th>قیمت خرید</th><th>قیمت باقی‌مانده</th><th>تاریخ خرید</th><th>از طرف</th><th>وضعیت</th><th>پروژه فعلی</th><th>عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedItems.map(e => (
                    <tr key={e._id}>
                      <td className="font-semibold text-gray-800">{e.name}</td>
                      <td className="text-gray-600">{EQUIPMENT_TYPES[e.type] || e.type}</td>
                      <td className="text-gray-500 text-sm">{[e.brand, e.model].filter(Boolean).join(' / ') || '---'}</td>
                      <td className="text-green-600 text-sm font-medium">{e.purchasePrice ? formatPrice(e.purchasePrice) : '---'}</td>
                      <td className="text-yellow-600">{formatPrice(e.remainingPrice)}</td>
                      <td className="text-gray-500 text-sm">{e.purchaseDate ? toShamsi(e.purchaseDate) : '---'}</td>
                      <td>
                        <span className={`badge ${
                          e.purchaseFrom === 'دفتر' ? 'bg-blue-100 text-blue-700' :
                          e.purchaseFrom === 'جعفری' ? 'bg-green-100 text-green-700' :
                          'bg-purple-100 text-purple-700'
                        }`}>
                          {e.purchaseFrom || 'دفتر'}
                        </span>
                      </td>
                      <td><Badge statusConfig={EQUIPMENT_STATUS[e.status]} /></td>
                      <td className="text-gray-500 text-sm">{e.assignedProject?.name || '---'}</td>
                      <td>
                        <div className="flex items-center gap-1">
                          <button onClick={() => setViewItem(e)} className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded-lg transition-all" title="جزئیات">
                            <MdVisibility size={15} />
                          </button>
                          {isManager && <button onClick={() => openEdit(e)} className="p-1.5 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-100 rounded-lg transition-all"><MdEdit size={15} /></button>}
                          {isManager && <button onClick={() => setDeleteId(e._id)} className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-100 rounded-lg transition-all"><MdDelete size={15} /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {pagedItems.map(e => (
              <div key={e._id} className="mobile-card">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-semibold text-gray-800">{e.name}</div>
                    <div className="text-xs text-gray-500">{EQUIPMENT_TYPES[e.type]} • {e.brand || e.model}</div>
                  </div>
                  <Badge statusConfig={EQUIPMENT_STATUS[e.status]} />
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                  {e.purchasePrice && <div><span className="text-gray-600">قیمت:</span> <span className="text-green-600">{formatPrice(e.purchasePrice)}</span></div>}
                  {e.purchaseDate && <div><span className="text-gray-600">تاریخ خرید:</span> <span className="text-gray-600">{toShamsi(e.purchaseDate)}</span></div>}
                  {e.purchaseFrom && <div><span className="text-gray-600">از طرف:</span> <span className="text-gray-600">{e.purchaseFrom}</span></div>}
                </div>
                {e.assignedProject && <div className="text-xs text-blue-600 mt-2">📁 {e.assignedProject.name}</div>}
                {isManager && (
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => openEdit(e)} className="flex-1 py-1.5 text-xs bg-gray-50 text-gray-800 rounded-lg">ویرایش</button>
                    <button onClick={() => setDeleteId(e._id)} className="flex-1 py-1.5 text-xs bg-red-100 text-red-600 rounded-lg">حذف</button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <Pagination page={page} totalPages={eqTotalPages} onChange={setPage} />
        </>
      )}

      {/* Add/Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editItem ? 'ویرایش تجهیز' : 'تجهیز جدید'} size="lg">
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <FormField label="نام تجهیز" required>
      <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="مثال: دوربین Sony A7III" />
    </FormField>
    
    {/* نوع تجهیز - از select به input تبدیل شد */}
    <FormField label="نوع تجهیز">
      <input className="form-input" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} placeholder="مثال: دوربین، لنز، نور، صدا..." />
    </FormField>
    
    <FormField label="برند">
      <input className="form-input" value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} placeholder="مثال: Sony, Canon, DJI" />
    </FormField>
    <FormField label="مدل">
      <input className="form-input" value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} placeholder="مثال: A7III, R5" />
    </FormField>
    
    {/* وضعیت با گزینه‌های جدید */}
    <FormField label="وضعیت">
      <select className="form-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
        <option value="Available">موجود</option>
        <option value="InUse">در استفاده</option>
        <option value="Maintenance">تعمیر</option>
        <option value="Lost">گم شده</option>
        <option value="Broken">خراب شده</option>
      </select>
    </FormField>
    
    <FormField label="از طرف">
      <select className="form-input" value={form.purchaseFrom} onChange={e => setForm({ ...form, purchaseFrom: e.target.value })}>
        <option value="دفتر">دفتر</option>
        <option value="جعفری">جعفری</option>
        <option value="ادیب">ادیب</option>
      </select>
    </FormField>
    
    <FormField label="قیمت خرید (افغانی)">
      <input type="number" className="form-input" value={form.purchasePrice} onChange={e => setForm({ ...form, purchasePrice: e.target.value })} placeholder="0" />
    </FormField>
    
    <FormField label="قیمت باقی‌مانده (افغانی)">
      <input type="number" className="form-input" value={form.remainingPrice} onChange={e => setForm({ ...form, remainingPrice: e.target.value })} placeholder="0" />
    </FormField>
    
    <FormField label="تاریخ خرید">
    <ShamsiDatePicker
    value={form.purchaseDate}
    onChange={(gregorianDate) => setForm({ ...form, purchaseDate: gregorianDate })}
    className="form-input w-full"
    placeholder="انتخاب تاریخ"
  />
    </FormField>
    
    <div className="sm:col-span-2">
      <FormField label="توضیحات">
        <textarea className="form-input h-20 resize-none" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="توضیحات اضافی..." />
      </FormField>
    </div>
  </div>
  <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-200">
    <button onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors text-sm">انصراف</button>
    <ActionBtn onClick={handleSave} label={saving ? 'ذخیره...' : editItem ? 'بروزرسانی' : 'ذخیره'} disabled={saving} />
  </div>
</Modal>

      {/* View Modal - جزئیات کامل */}
{/* View Modal - جزئیات کامل */}
{viewItem && (
  <Modal isOpen={!!viewItem} onClose={() => setViewItem(null)} title="جزئیات تجهیز" size="md">
    <div className="space-y-4">
      <div className="flex justify-between items-start border-b border-gray-200 pb-3">
        <h2 className="text-xl font-bold text-gray-800">{viewItem.name}</h2>
        <Badge statusConfig={getStatusConfig(viewItem.status)} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><div className="text-xs text-gray-600">نوع</div><div className="text-gray-800">{viewItem.type || '---'}</div></div>
        <div><div className="text-xs text-gray-600">برند / مدل</div><div className="text-gray-800">{[viewItem.brand, viewItem.model].filter(Boolean).join(' / ') || '---'}</div></div>
        <div><div className="text-xs text-gray-600">قیمت خرید</div><div className="text-green-600 font-bold">{formatCurrency(viewItem.purchasePrice)}</div></div>
        <div><div className="text-xs text-gray-600">قیمت باقی‌مانده</div><div className="text-yellow-600 font-bold">{formatCurrency(viewItem.remainingPrice)}</div></div>
        <div><div className="text-xs text-gray-600">تاریخ خرید</div><div className="text-gray-800">{viewItem.purchaseDate ? toShamsi(viewItem.purchaseDate) : '---'}</div></div>
        <div><div className="text-xs text-gray-600">از طرف</div><div className="text-gray-800">{viewItem.purchaseFrom || 'دفتر'}</div></div>
        <div><div className="text-xs text-gray-600">وضعیت</div><div className="text-gray-800">{getStatusLabel(viewItem.status)}</div></div>
        <div><div className="text-xs text-gray-600">پروژه فعلی</div><div className="text-gray-800">{viewItem.assignedProject?.name || '---'}</div></div>
      </div>
      {viewItem.notes && (
        <div>
          <div className="text-xs text-gray-600">توضیحات</div>
          <p className="text-gray-600 text-sm bg-gray-50 p-3 rounded-xl mt-1">{viewItem.notes}</p>
        </div>
      )}
    </div>
  </Modal>
)}

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="حذف تجهیز" message="آیا مطمئن هستید که میخواهید این تجهیز را حذف کنید؟" />
    </div>
  );
}